import { AddCardDialog } from '@/components/billing/add-card-dialog'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { quoteTotal } from '@/lib/billing-format'
import { billingApi } from '@/services/billing'
import { useAuthStore } from '@/stores/auth-store'

// Shows trial-days-remaining + an add-card CTA on the dashboard, and bounces
// to the upgrade wall if the org becomes locked mid-session (login.tsx
// already handles the common case right after sign-in).
export function TrialBanner() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const [cardOpen, setCardOpen] = useState(false)

  const billingQuery = useQuery({
    queryKey: ['billing-status'],
    queryFn: billingApi.getStatus,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const status = billingQuery.data

  useEffect(() => {
    if (status?.is_locked) {
      navigate('/billing/locked', { replace: true })
    }
  }, [status?.is_locked, navigate])

  // A failed payment, on any plan: the owner can fix it here.
  if (status?.card?.payment_failed && !status.is_locked) {
    return (
      <div className="mb-4 flex flex-col items-start justify-between gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 sm:flex-row sm:items-center">
        <p className="text-sm text-foreground">
          Your last FacilityPro payment didn’t go through.
          <span className="text-muted-foreground">
            {status.can_manage_card
              ? ' Update your card and we’ll charge the outstanding amount straight away.'
              : ' Ask your account owner to update the payment card.'}
          </span>
        </p>
        {status.can_manage_card ? (
          <Button type="button" size="sm" variant="destructive" onClick={() => setCardOpen(true)}>
            Update card
          </Button>
        ) : null}
        <AddCardDialog
          open={cardOpen}
          onOpenChange={setCardOpen}
          title="Update your payment card"
          description="Future bills go to this card, and the payment that failed is charged to it now."
        />
      </div>
    )
  }

  // Only a running trial gets the countdown; paid and card-exempt orgs see nothing.
  if (!status || status.plan !== 'trial' || status.is_locked) {
    return null
  }

  const daysRemaining = status.trial_days_remaining ?? 0
  const card = status.card
  const firstBill = card ? (card.interval === 'year' ? status.price?.year : status.price?.month) : null

  return (
    <div className="mb-4 flex flex-col items-start justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 sm:flex-row sm:items-center">
      <p className="text-sm text-foreground">
        {daysRemaining === 0
          ? 'Your free trial ends today.'
          : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in your free trial.`}
        <span className="text-muted-foreground">
          {card
            ? ` Card ending ${card.last4} is on file; you won’t be charged during the trial${
                firstBill ? `, then ${quoteTotal(firstBill)} for your team today` : ''
              }.`
            : status.card_exempt
              ? ' No payment card is needed for organizations in your country yet.'
              : ' Add a payment card before it ends so your team can keep signing in.'}
        </span>
      </p>
      {/* Card-exempt countries (e.g. India) can't put a card on file yet. */}
      {!card && !status.card_exempt && status.can_manage_card ? (
        <Button type="button" size="sm" onClick={() => setCardOpen(true)}>
          Add payment card
        </Button>
      ) : null}
      <AddCardDialog open={cardOpen} onOpenChange={setCardOpen} />
    </div>
  )
}
